import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import * as api from '../services/api'

const ChatContext = createContext()

export function useChat() {
  const context = useContext(ChatContext)
  if (!context) {
    throw new Error('useChat must be used within ChatProvider')
  }
  return context
}

export function ChatProvider({ children }) {
  const { user, getAccessTokenSilently, logout } = useAuth0()

  // Core state
  const [conversationId, setConversationId] = useState(null)
  const [messages, setMessages] = useState([])
  const [messageCounter, setMessageCounter] = useState(0)
  const [isStreaming, setIsStreaming] = useState(false)
  const currentEventSource = useRef(null)

  // Conversation history
  const [conversations, setConversations] = useState([])
  const [loadingConversations, setLoadingConversations] = useState(false)

  // UI state
  const [errorMessage, setErrorMessage] = useState(null)
  const [progressStages, setProgressStages] = useState([])
  const [showTyping, setShowTyping] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

  // Mode state
  const [currentMode, setCurrentMode] = useState('conversation')

  // Profile state
  const [userProfile, setUserProfile] = useState(null)

  // Initialize from URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const cid = urlParams.get('cid')
    if (cid) {
      setConversationId(cid)
      loadConversationHistory(cid)
    }
  }, [])

  // Load conversations on mount
  useEffect(() => {
    loadConversations()
    loadProfile()
  }, [])

  // Mobile sidebar handling
  useEffect(() => {
    if (window.innerWidth <= 1024) {
      setSidebarCollapsed(true)
    }
  }, [])

  // API Functions
  const getToken = async () => {
    try {
      return await getAccessTokenSilently()
    } catch (error) {
      console.error('Failed to get access token:', error)
      throw error
    }
  }

  const loadProfile = useCallback(async () => {
    try {
      const token = await getToken()
      const profile = await api.getProfile(token)
      setUserProfile(profile)
    } catch (error) {
      console.error('Failed to load profile:', error)
      setUserProfile(null)
    }
  }, [])

  const saveProfile = useCallback(async (profileData) => {
    try {
      const token = await getToken()
      const updatedProfile = await api.updateProfile(token, profileData)
      setUserProfile(updatedProfile)
      return updatedProfile
    } catch (error) {
      console.error('Failed to save profile:', error)
      throw error
    }
  }, [])

  const loadConversations = useCallback(async (searchQuery = '') => {
    setLoadingConversations(true)
    try {
      const token = await getToken()
      const data = await api.listConversations(token, searchQuery)
      setConversations(data.conversations || [])
    } catch (error) {
      console.error('Failed to load conversations:', error)
      setConversations([])
    } finally {
      setLoadingConversations(false)
    }
  }, [])

  const loadConversationHistory = useCallback(async (cid) => {
    try {
      const token = await getToken()
      const data = await api.getConversationMessages(token, cid)

      const formattedMessages = data.messages.map(msg => ({
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp
      }))

      setMessages(formattedMessages)
      setMessageCounter(formattedMessages.filter(m => m.role === 'user').length)
    } catch (error) {
      console.error('Failed to load conversation history:', error)
      if (error.status === 404 || error.status === 403) {
        // Show error message that conversation doesn't exist
        setErrorMessage({
          message: 'هذه المحادثة غير موجودة',
          details: 'سيتم توجيهك إلى محادثة جديدة',
          status: error.status
        })
        // Clear conversation state and URL
        setConversationId(null)
        setMessages([])
        setMessageCounter(0)
        setProgressStages([])
        setShowTyping(false)

        // Clear URL - update to base URL without query params
        const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`
        window.history.replaceState({}, '', newUrl)

        // Reload conversations
        loadConversations()

        // Clear error after 5 seconds
        setTimeout(() => setErrorMessage(null), 5000)
      }
    }
  }, [loadConversations])

  const loadConversationById = useCallback(async (cid) => {
    if (isStreaming) return

    setConversationId(cid)
    setMessages([])
    setMessageCounter(0)

    // Update URL
    const newUrl = new URL(window.location)
    newUrl.searchParams.set('cid', cid)
    window.history.pushState({}, '', newUrl)

    await loadConversationHistory(cid)

    // Close sidebar on mobile
    if (window.innerWidth <= 1024) {
      setSidebarCollapsed(true)
    }
  }, [isStreaming, loadConversationHistory])

  const renameConversation = useCallback(async (cid, newTitle) => {
    try {
      const token = await getToken()
      await api.updateConversation(token, cid, { title: newTitle })
      setConversations(prev =>
        prev.map(conv =>
          conv.id === cid ? { ...conv, title: newTitle } : conv
        )
      )
    } catch (error) {
      console.error('Failed to rename conversation:', error)
      throw error
    }
  }, [])

  const deleteConversation = useCallback(async (cid) => {
    try {
      const token = await getToken()
      await api.deleteConversation(token, cid)
      setConversations(prev => prev.filter(conv => conv.id !== cid))

      if (cid === conversationId) {
        startNewConversation()
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error)
      throw error
    }
  }, [conversationId])

  const startNewConversation = useCallback(() => {
    if (isStreaming) return

    setConversationId(null)
    setMessages([])
    setMessageCounter(0)
    setProgressStages([])
    setShowTyping(false)
    setErrorMessage(null)

    // Clear URL - update to base URL without query params
    const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`
    window.history.replaceState({}, '', newUrl)

    // Reload conversations
    loadConversations()

    // Close sidebar on mobile
    if (window.innerWidth <= 1024) {
      setSidebarCollapsed(true)
    }
  }, [isStreaming, loadConversations])

  const sendMessage = useCallback(async (userMessage) => {
    if (isStreaming) return

    setErrorMessage(null)

    // Add user message to UI optimistically
    setMessages(prev => [...prev, { role: 'user', content: userMessage }])

    const clientMessageId = messageCounter === 0 && !conversationId
      ? 'abc-123'
      : `abc-123-${Date.now()}`

    const isFirstMessage = messageCounter === 0 && !conversationId

    try {
      const token = await getToken()
      const response = await api.sendMessage(token, {
        user_message: userMessage,
        client_message_id: clientMessageId,
        conversation_id: conversationId,
        user_id: user.sub,
        mode: currentMode
      })

      // If new conversation, update state and URL
      if (!conversationId && response.conversation_id) {
        setConversationId(response.conversation_id)
        const newUrl = new URL(window.location)
        newUrl.searchParams.set('cid', response.conversation_id)
        window.history.replaceState({}, '', newUrl)
        await loadConversations()

        // Generate title for new conversation
        if (isFirstMessage) {
          generateTitle(response.conversation_id, userMessage, token)
        }
      }

      // Start streaming
      if (response.message_id) {
        startStreaming(response.conversation_id || conversationId, response.message_id, token)
      }

      setMessageCounter(prev => prev + 1)
    } catch (error) {
      console.error('Send message error:', error)

      // Remove the user message from UI since it was never stored in the database
      setMessages(prev => prev.slice(0, -1))

      if (error.status === 403) {
        startNewConversation()
      } else {
        // Store detailed error message
        setErrorMessage({
          message: error.message,
          details: error.details,
          status: error.status
        })
        // Clear error after 10 seconds
        setTimeout(() => setErrorMessage(null), 10000)
      }
    }
  }, [isStreaming, conversationId, messageCounter, currentMode, user, loadConversations])

  const generateTitle = useCallback(async (convId, firstMessage, token) => {
    try {
      // Generate a simple title from the first message (first 50 characters)
      let generatedTitle = firstMessage.substring(0, 50).trim()
      if (firstMessage.length > 50) {
        generatedTitle += '...'
      }

      // Try to call the backend API to save the title
      try {
        await api.updateConversation(token, convId, { title: generatedTitle })
      } catch (apiError) {
        console.error('Failed to save title to backend:', apiError)
        // Continue anyway - at least we have it locally
      }

      // Update the conversation in the list with the new title
      setConversations(prev =>
        prev.map(conv =>
          conv.id === convId ? { ...conv, title: generatedTitle } : conv
        )
      )
    } catch (error) {
      console.error('Failed to generate title:', error)
      // Silently fail - not critical
    }
  }, [])

  const startStreaming = useCallback((cid, messageId, token) => {
    // Close any existing stream
    if (currentEventSource.current) {
      currentEventSource.current.close()
      currentEventSource.current = null
    }

    setIsStreaming(true)
    setProgressStages([])
    setShowTyping(true)

    const streamUrl = `/api/v1/conversations/${cid}/stream?message_id=${messageId}&mode=${currentMode}&access_token=${encodeURIComponent(token)}`
    const eventSource = new EventSource(streamUrl)

    let assistantMessage = ''
    let messageAdded = false

    // Phase events
    eventSource.addEventListener('phase', (event) => {
      try {
        const data = JSON.parse(event.data)
        if (data.stage) {
          addProgressStage(data.stage)
        }
      } catch (error) {
        console.error('Phase event error:', error)
      }
    })

    // Answer delta events
    eventSource.addEventListener('answer_delta', (event) => {
      try {
        const data = JSON.parse(event.data)
        if (data.text) {
          assistantMessage += data.text

          if (!messageAdded) {
            setMessages(prev => [...prev, { role: 'assistant', content: assistantMessage }])
            messageAdded = true
            setShowTyping(false)
          } else {
            setMessages(prev => {
              const newMessages = [...prev]
              const lastMessage = newMessages[newMessages.length - 1]
              if (lastMessage && lastMessage.role === 'assistant') {
                lastMessage.content = assistantMessage
              }
              return newMessages
            })
          }
        }
      } catch (error) {
        console.error('Answer delta error:', error)
      }
    })

    // Answer done event
    eventSource.addEventListener('answer_done', () => {
      setShowTyping(false)
      setProgressStages([])
      finishStreaming()
    })

    // Error handling
    eventSource.onerror = (error) => {
      console.error('SSE error:', error)
      finishStreaming()
      setErrorMessage({
        message: 'حدث خطأ أثناء تلقي الرد',
        details: 'يرجى المحاولة مرة أخرى',
        status: null
      })
      setTimeout(() => setErrorMessage(null), 5000)
    }

    currentEventSource.current = eventSource
  }, [currentMode])

  const finishStreaming = useCallback(() => {
    setIsStreaming(false)
    setShowTyping(false)

    if (currentEventSource.current) {
      currentEventSource.current.close()
      currentEventSource.current = null
    }
  }, [])

  const addProgressStage = useCallback((stage) => {
    const stageMap = {
      'detection': 'اكتشاف نوع السؤال',
      'queries': 'توليد الاستعلامات',
      'retrieval': 'استرجاع المواد',
      'scored': 'تقييم النتائج',
      'selection': 'اختيار المواد',
      'answering': 'صياغة الإجابة'
    }

    const arabicText = stageMap[stage]
    if (arabicText) {
      setProgressStages(prev => [...prev, { text: arabicText, stage }])
    }
  }, [])

  const value = {
    // Core state
    conversationId,
    messages,
    isStreaming,
    currentMode,
    setCurrentMode,

    // Conversations
    conversations,
    loadingConversations,
    loadConversations,
    loadConversationById,
    renameConversation,
    deleteConversation,
    startNewConversation,

    // Messages
    sendMessage,

    // UI state
    errorMessage,
    progressStages,
    showTyping,
    sidebarCollapsed,
    setSidebarCollapsed,

    // Profile
    userProfile,
    saveProfile,

    // Auth
    user,
    logout: () => logout({ logoutParams: { returnTo: window.location.origin } })
  }

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  )
}
